import { useContext, useEffect, useMemo, useRef } from 'react';
import { shallowEqual } from 'react-redux';

import {
  DetailedIconEmail,
  DetailedIconForSaleSign,
  DetailedIconHandshake,
  DetailedIconPiggybank,
  Flex,
} from '@zillow/constellation';
import { Button, Heading, Paragraph, Text, TextButton } from '@zillow/constellation-10';

import { logError, useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get } from '@zg-rentals/rental-manager-env';

import zillowHouse from '../../../../../images/zillow-house.png';

import { RMWContext } from '../../../../../providers/RMWContext';

import { useSelector } from '../../../../../selectors/useSelector';

import accessUtils from '../../../../../utils/accessUtils';

import SelectAvailableActionsCSEvents from '../../../../../model/ClickstreamEvent/SelectAvailableActionsCSEvents';

import SpinnerContainer from '../../../../SpinnerContainer';

import { StyledBlueBackgroundFlex, StyledBorderedSpacer, StyledCenteredImage } from './StyledComponents';

import type {
  SelectAvailableActionsCSEventsProps,
  SelectAvailableActionsTriggerObject,
} from '../../../../../model/ClickstreamEvent/SelectAvailableActionsCSEvents';

type Props = {
  listingAlias: string;
  triggerObject?: SelectAvailableActionsTriggerObject;
};

const SelectAvailableActions = ({ listingAlias, triggerObject = 'posting_path' }: Props) => {
  const { paths } = useContext(RMWContext);

  const {
    app: { appConfig },
    user,
  } = useSelector((state) => ({ app: state.app, user: state.user }), shallowEqual);

  const { data: webAnalyticsData, isLoading: isUseWebAnalyticsLoading } =
    useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
      {
        listingAlias,
        includeLeases: false,
      },
      { enabled: Boolean(listingAlias) },
    );

  const csEventParams = useMemo(() => {
    if (!webAnalyticsData || Object.keys(webAnalyticsData).length === 0) {
      logError({
        error: 'webAnalyticsData is empty:',
        errorType: 'SelectAvailableActions',
        context: `SelectAvailableActions@csEventParams. listingAlias: ${listingAlias}`,
      });
    }

    return { currentProperty: webAnalyticsData ?? {}, user, triggerObject };
  }, [webAnalyticsData, user, triggerObject]);

  const topMostElementRef = useRef<HTMLImageElement>();

  const canAccessNoWelcomePageML = accessUtils.canAccessNoWelcomePageML({ appConfig });

  useEffect(() => {
    topMostElementRef?.current?.scrollIntoView(false);
  }, []);

  const handleLinkCreateListingClick = () => {
    SelectAvailableActionsCSEvents.CreateListing(csEventParams as SelectAvailableActionsCSEventsProps);
  };

  const handleLinkApplicationClick = () => {
    SelectAvailableActionsCSEvents.InviteToApply(csEventParams as SelectAvailableActionsCSEventsProps);
  };

  const handleLinkLeaseClick = () => {
    SelectAvailableActionsCSEvents.AddLease(csEventParams as SelectAvailableActionsCSEventsProps);
  };

  const handleLinkPaymentClick = () => {
    SelectAvailableActionsCSEvents.SetUpPayments(csEventParams as SelectAvailableActionsCSEventsProps);
  };

  return (
    <>
      <StyledCenteredImage ref={topMostElementRef} src={zillowHouse} alt={'house'} paddingBottom={{ xs: 'sm' }} />

      {isUseWebAnalyticsLoading ? (
        <SpinnerContainer />
      ) : (
        <>
          <Heading level={4} css={{ textAlign: 'center', paddingBottom: { lg: '100' } }}>
            You added your property
          </Heading>
          <Paragraph css={{ textAlign: 'center', marginBottom: '800' }}>
            {canAccessNoWelcomePageML ? (
              <>
                You&apos;re one step closer to listing on our networks
                <br />
                with over 1 million daily visitors across Zillow, Trulia, and HotPads.
              </>
            ) : (
              'Now you can list it for rent and use Zillow Rental Manager to fill your vacancy.'
            )}
          </Paragraph>
          <StyledBlueBackgroundFlex
            display="flex"
            flexDirection={{ xs: 'column', lg: 'row' }}
            alignItems="flex-start"
            justifyContent="space-around"
            gap="sm"
            // Padding is for blue section
            paddingY={{ xs: 'md', lg: 'lg' }}
            paddingX="sm"
            // Margins is to make gaps around the blue section
            marginX={{ lg: 9, xl: 12 }}
            marginBottom={{ xs: 'sm', lg: 'md' }}
          >
            <Flex display="flex" flexDirection="row" gap={{ xs: 'xs', xl: 'sm' }}>
              <DetailedIconForSaleSign size={{ xs: 'lg', md: 'md' }} />
              <div>
                <Text textStyle="body-bold" asChild>
                  <p>List your property for rent</p>
                </Text>
                <Text>
                  {canAccessNoWelcomePageML
                    ? `It's free to post and takes just a few minutes!`
                    : 'It takes just minutes to create and post your listing.'}
                </Text>
              </div>
            </Flex>
            <Button
              data-testid="create-listing-button"
              tone="brand"
              emphasis="filled"
              asChild
              onClick={handleLinkCreateListingClick}
              css={{
                marginLeft: { base: '400', md: '0' },
              }}
              data-eventid={SelectAvailableActionsCSEvents.CreateListing.data_event_id}
            >
              <a href={`${paths.propertiesPage}/${listingAlias}/${paths.propertyDetailsListing}`}>Create Listing</a>
            </Button>
          </StyledBlueBackgroundFlex>
          <Flex
            display="flex"
            flexDirection={{ xs: 'column', lg: 'row' }}
            alignItems="stretch"
            justifyContent="space-between"
            gap={{ xs: 'sm', lg: 'md' }}
            marginX={{ lg: 9, xl: 12 }}
            marginBottom={{ lg: 'xl' }}
          >
            <StyledBorderedSpacer padding={'sm'}>
              <Flex
                display="flex"
                flexDirection={{ xs: 'row', lg: 'column' }}
                alignItems="flex-start"
                justifyContent={{ xs: 'flex-start', lg: 'space-between' }}
                gap="xs"
              >
                <DetailedIconEmail size={{ xs: 'lg', md: 'md' }} />
                <div>
                  <Text
                    asChild
                    css={{ marginBlock: { lg: '200' }, marginRight: { base: '800', lg: '600', xl: '400' } }}
                  >
                    <p>Screen renters with our hassle-free applications</p>
                  </Text>
                  <TextButton
                    asChild
                    css={{ marginTop: '200', fontSize: { base: 'body', lg: 'body-sm' } }}
                    onClick={handleLinkApplicationClick}
                    data-eventid={SelectAvailableActionsCSEvents.InviteToApply.data_event_id}
                  >
                    <a href={`${paths.base}/${paths.propertyDetailsApplications}?propertyId=${listingAlias}`}>
                      Invite someone to apply
                    </a>
                  </TextButton>
                </div>
              </Flex>
            </StyledBorderedSpacer>
            <StyledBorderedSpacer padding={'sm'}>
              <Flex
                display="flex"
                flexDirection={{ xs: 'row', lg: 'column' }}
                alignItems="flex-start"
                justifyContent={{ xs: 'flex-start', lg: 'space-between' }}
                gap="xs"
              >
                <DetailedIconHandshake size={{ xs: 'lg', md: 'md' }} />
                <div>
                  <Text
                    asChild
                    css={{ marginBlock: { lg: '200' }, marginRight: { base: '800', lg: '600', xl: '400' } }}
                  >
                    <p>Upload documents or create a custom lease for signing</p>
                  </Text>
                  <TextButton
                    asChild
                    css={{ marginTop: '200', fontSize: { base: 'body', lg: 'body-sm' } }}
                    onClick={handleLinkLeaseClick}
                    data-eventid={SelectAvailableActionsCSEvents.AddLease.data_event_id}
                  >
                    <a href={`${paths.leasing}/property/${listingAlias}`}>Add a lease</a>
                  </TextButton>
                </div>
              </Flex>
            </StyledBorderedSpacer>
            <StyledBorderedSpacer padding={'sm'}>
              <Flex
                display="flex"
                flexDirection={{ xs: 'row', lg: 'column' }}
                alignItems="flex-start"
                justifyContent={{ xs: 'flex-start', lg: 'space-between' }}
                gap="xs"
              >
                <DetailedIconPiggybank size={{ xs: 'md', md: 'md' }} />
                <div>
                  <Text
                    asChild
                    css={{ marginBlock: { lg: '200' }, marginRight: { base: '800', lg: '600', xl: '400' } }}
                  >
                    <p>Collect rent payments at no cost to you</p>
                  </Text>
                  <TextButton
                    asChild
                    css={{ marginTop: '200', fontSize: { base: 'body', lg: 'body-sm' } }}
                    onClick={handleLinkPaymentClick}
                    data-eventid={SelectAvailableActionsCSEvents.SetUpPayments.data_event_id}
                  >
                    <a href={`${paths.base}/${paths.propertyDetailsPayments}/${paths.properties}/${listingAlias}`}>
                      Set up payments
                    </a>
                  </TextButton>
                </div>
              </Flex>
            </StyledBorderedSpacer>
          </Flex>
        </>
      )}
    </>
  );
};

export default SelectAvailableActions;
